import React, { useCallback, useEffect, useState } from 'react';
import useAxios from '../../../hooks/useAxios';
import { AxiosResponse } from 'axios';
import type { TSubmittableData } from '../../../types/stencil';
import type { CancellableAxiosInstance } from '../../../utils/axiosInstance';

import useSubmittableData from '../../../hooks/stencil/useSubmittableData';
import { useSelector } from 'react-redux';
import type { TStencilStore } from '../../../types/Stencil';

import { handleError } from '../../../utils/error';
import { has } from '../../../utils/typescript';

interface TFailedSubmissionResponse {
  error: string;
}

interface TSuccessfulSubmissionResponse {
  // eslint-disable-next-line camelcase
  redirect_location: string;
}

type TResponse = TFailedSubmissionResponse | TSuccessfulSubmissionResponse;

function isFailedResponse(
  response: TResponse
): response is TFailedSubmissionResponse {
  return has('error', { ...response });
}

declare global {
  interface Window {
    allowUnload: boolean;
    unwindPageNavigation: () => void;
  }
}

class StencilSubmitter {
  _axios: CancellableAxiosInstance | Record<string, never>;
  _data: TSubmittableData;
  _$form: JQuery;
  _updating: boolean;
  _url: string | null | undefined;

  constructor(
    axios: CancellableAxiosInstance | Record<string, never>,
    data: TSubmittableData,
    updatingExistingSubmission: boolean
  ) {
    this._axios = axios;
    this._data = data;
    this._updating = updatingExistingSubmission;
    this._$form = $('.stencil-form-element');
    this._url = this._getSubmissionUrl();
  }

  get form() {
    return this._$form[0];
  }

  start(onSuccess: () => void, onFailure: () => void) {
    if (this._updating) {
      return this._update(onSuccess, onFailure);
    } else {
      return this._create(onSuccess, onFailure);
    }
  }

  _update(onSuccess: () => void, onFailure: () => void) {
    return this._axios
      .patch<{ error?: string }>(this._url, this._data)
      .then(this._handleResponse.bind(this))
      .then((res) => {
        res.data.error ? onFailure() : onSuccess();
      })
      .catch((error) => {
        alert('There was a problem saving your submission. Please try again.');
        onFailure();
        handleError(error);
      });
  }

  _create(onSuccess: () => void, onFailure: () => void) {
    return this._axios
      .post<{ error?: string }>(this._url, this._data)
      .then(this._handleResponse.bind(this))
      .then((res) => {
        res.data && res.data.error ? onFailure() : onSuccess();
      })
      .catch((error) => {
        alert('There was a problem saving your submission. Please try again.');
        onFailure();
        handleError(error);
      });
  }

  _handleResponse(
    response: AxiosResponse<TResponse>
  ): AxiosResponse<TResponse> {
    const { data } = response;

    if (isFailedResponse(data)) {
      this._handleFailure(data);
    } else {
      this._handleSuccess(data);
    }

    return response;
  }

  // eslint-disable-next-line camelcase
  _handleSuccess(data: { redirect_location: string }) {
    this._allowLeavingPage();
    this._followRedirect(data.redirect_location);
  }

  _handleFailure(data: TFailedSubmissionResponse) {
    this._scrollUp();
    this._clearErrors();
    this._displayError(data.error);
  }

  _allowLeavingPage() {
    window.allowUnload = true;
    window.unwindPageNavigation();
  }

  _followRedirect(url: string) {
    window.location.href = url;
  }

  _getSubmissionUrl(): string {
    return this._$form.prop('action') as string;
  }

  _clearErrors() {
    this._$form.find('.alert.alert-danger').remove();
  }

  _displayError(errorMessage: string) {
    const $flash = $(
      '\n' +
        '<div class="alert alert-danger">\n' +
        '  <button type="button" class="close alert-close" data-dismiss="alert">&times;</button>\n' +
        '  <span class="alert-message">\n' +
        '    ' +
        errorMessage +
        '\n' +
        '  </span>\n' +
        '</div>\n'
    );

    this._$form.prepend($flash);
  }

  _scrollUp() {
    $(window).scrollTop(0);
  }
}

function useSubmitter() {
  const updating = useSelector(
    (state: TStencilStore) => state.settings.updatingExistingSubmission
  );
  const axios = useAxios();
  const data = useSubmittableData();
  const [submitter, setSubmitter] = useState(
    new StencilSubmitter(axios, data, updating)
  );

  useEffect(() => {
    setSubmitter(new StencilSubmitter(axios, data, updating));
  }, [axios, data, updating]);

  return submitter;
}

function Submit({ enabled }: { enabled: boolean }): React.ReactElement {
  const submitter = useSubmitter();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEmailValid = useCallback(() => {
    const emailInput = submitter.form.querySelector<HTMLInputElement>(
      '#stencil_author_email'
    );

    if (typeof emailInput.checkValidity !== 'function') return true;

    return emailInput.checkValidity();
  }, [submitter]);

  const notifyAboutInvalidEmail = useCallback(() => {
    const emailInput = submitter.form.querySelector<HTMLInputElement>(
      '#stencil_author_email'
    );

    if (typeof emailInput.reportValidity === 'function') {
      emailInput.reportValidity();
    } else {
      alert(
        'The email address you provided looks like it is invalid. Please provide a valid one, or email team@newlywords.com if this is incorrect.'
      );
    }
  }, [submitter]);

  const handleSuccess = useCallback(() => {
    // no-op while we wait for redirect
  }, []);

  const handleFailure = useCallback(() => {
    setIsSubmitting(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isEmailValid()) {
      setIsSubmitting(true);
      await submitter.start(handleSuccess, handleFailure);
    } else {
      notifyAboutInvalidEmail();
    }
  }, [
    handleFailure,
    handleSuccess,
    isEmailValid,
    notifyAboutInvalidEmail,
    submitter
  ]);

  return (
    <button
      type="button"
      className="wysiwyg-editor-button"
      disabled={isSubmitting || !enabled}
      onClick={handleSubmit}
    >
      {isSubmitting ? 'Submitting...' : 'Submit'}
    </button>
  );
}

export default Submit;
